import React, { useState, useEffect } from 'react';
import { Button, Input, Typography, SideModal } from 'design_system/src';
import {
	User,
	Role,
	CreateUserDto,
	UpdateUserDto,
} from 'models/user-management/interfaces/user-management.interface';
import {
	createUser,
	updateUser,
	fetchRoles,
	fetchCompanyVehicles,
} from 'models/user-management/controllers/users.controller';
import { fetchCompanies as fetchCompaniesList } from 'models/user-management/controllers/companies.controller';
import { toasterNotification } from 'models/shared/helpers/toasterNotification';
import { toasterOptions } from 'models/shared/enums/toaster.enum';
import { extractErrorMessage } from 'models/shared/helpers/errors';
import { Company } from 'models/companies/interfaces/companies.interface';
import { UserType } from 'models/user-management/enums/user-management.enum';
import { Vehicle } from 'models/vehicles/interfaces/vehicles.interface';

interface EditUserProps {
	user?: User;
	onClose: () => void;
	onSave: () => void;
}

type UserFormData = CreateUserDto & UpdateUserDto;
type VehicleAssignmentMode = 'single' | 'multiple';

function EditUser({ user, onClose, onSave }: EditUserProps) {
	const [open, setOpen] = useState(true);
	const [formData, setFormData] = useState<UserFormData>({
		email: '',
		name: '',
		lastName: '',
		phone: '',
		company: '',
		role: [],
		password: '',
		type: UserType.CLIENT,
		vehicles: [],
	});
	const [roles, setRoles] = useState<Role[]>([]);
	const [companies, setCompanies] = useState<Company[]>([]);
	const [companyVehicles, setCompanyVehicles] = useState<Vehicle[]>([]);
	const [loadingVehicles, setLoadingVehicles] = useState(false);
	const [vehiclesLoadError, setVehiclesLoadError] = useState<string | null>(
		null,
	);
	const [vehicleAssignmentMode, setVehicleAssignmentMode] =
		useState<VehicleAssignmentMode>('single');
	const [loading, setLoading] = useState(false);
	const [imageFile, setImageFile] = useState<File | undefined>();

	const selectedVehicleIds = Array.isArray(formData.vehicles)
		? formData.vehicles.map(String)
		: [];
	const singleSelectedVehicleId = selectedVehicleIds[0] ?? '';

	const handleClose = (value: boolean) => {
		setOpen(value);
		if (!value) {
			onClose();
		}
	};

	useEffect(() => {
		loadRoles();
		loadCompanies();
	}, []);

	useEffect(() => {
		if (user) {
			setFormData({
				email: user.email || '',
				name: user.name || '',
				lastName: user.lastName || '',
				phone: user.phone || '',
				company:
					typeof user.company === 'object'
						? user.company._id
						: user.company || '',
				role: user.role || [],
				isActive: user.isActive,
				type: user.type || UserType.CLIENT,
				vehicles: (user.vehicles ?? []).map((v) =>
					typeof v === 'object' && v !== null && '_id' in v ? v._id : String(v),
				),
			});
			const vehicleIds = (user.vehicles ?? []).map((v) =>
				typeof v === 'object' && v !== null && '_id' in v ? v._id : String(v),
			);
			setVehicleAssignmentMode(vehicleIds.length > 1 ? 'multiple' : 'single');
		}
	}, [user]);

	useEffect(() => {
		if (formData.company) {
			loadCompanyVehicles(formData.company);
		} else {
			setCompanyVehicles([]);
		}
	}, [formData.company]);

	const loadRoles = async () => {
		try {
			const data = await fetchRoles();
			// Ensure data is an array
			setRoles(Array.isArray(data) ? data : []);
		} catch (error: any) {
			console.error('Error loading roles:', error);
			setRoles([]);
			toasterNotification({
				msg: error?.message || 'Error loading roles',
				toasterType: toasterOptions.error,
			});
		}
	};

	const loadCompanyVehicles = async (companyId: string) => {
		setLoadingVehicles(true);
		setVehiclesLoadError(null);
		try {
			const list = await fetchCompanyVehicles(companyId);
			setCompanyVehicles(
				(list || []).map((v) => ({
					_id: String(v._id),
					internalId: v.internalId,
					description: v.description,
					status: v.status as Vehicle['status'],
					company: companyId,
					tile: {} as Vehicle['tile'],
					createdAt: '',
					updatedAt: '',
					image: {} as Vehicle['image'],
				})),
			);
		} catch (error: any) {
			setCompanyVehicles([]);
			setVehiclesLoadError(
				extractErrorMessage(error) ||
					'Could not load vehicles for this company',
			);
		} finally {
			setLoadingVehicles(false);
		}
	};

	const loadCompanies = async () => {
		try {
			const data = await fetchCompaniesList();
			setCompanies(data);
		} catch (error: any) {
			toasterNotification({
				msg: error?.message || 'Error loading companies',
				toasterType: toasterOptions.error,
			});
		}
	};

	const handleChange = (field: keyof UserFormData, value: any) => {
		setFormData((prev) => ({ ...prev, [field]: value }));
	};

	const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		if (e.target.files && e.target.files[0]) {
			setImageFile(e.target.files[0]);
		}
	};

	const handleSubmit = async () => {
		setLoading(true);
		try {
			if (user) {
				let roleArray: string[] = [];
				if (Array.isArray(formData.role)) {
					roleArray = formData.role;
				} else if (formData.role) {
					roleArray = [formData.role];
				}
				if (
					formData.type === UserType.DRIVER &&
					(!formData.vehicles || formData.vehicles.length === 0)
				) {
					toasterNotification({
						msg: 'Please assign at least one vehicle for driver users',
						toasterType: toasterOptions.error,
					});
					setLoading(false);
					return;
				}

				const updateData: UpdateUserDto = {
					email: formData.email,
					name: formData.name,
					lastName: formData.lastName,
					phone: formData.phone,
					company: formData.company,
					role: roleArray,
					isActive: formData.isActive,
					type: formData.type,
					...(formData.type === UserType.DRIVER
						? { vehicles: formData.vehicles }
						: { vehicles: [] }),
				};
				const userId =
					typeof user._id === 'string' ? user._id : String(user._id);
				await updateUser(userId, updateData, imageFile);
				toasterNotification({
					msg: 'User updated successfully',
					toasterType: toasterOptions.success,
				});
			} else {
				let roleArray: string[] = [];
				if (Array.isArray(formData.role)) {
					roleArray = formData.role;
				} else if (formData.role) {
					roleArray = [formData.role];
				}

				if (roleArray.length === 0) {
					toasterNotification({
						msg: 'Please select at least one role',
						toasterType: toasterOptions.error,
					});
					setLoading(false);
					return;
				}

				if (
					formData.type === UserType.DRIVER &&
					(!formData.vehicles || formData.vehicles.length === 0)
				) {
					toasterNotification({
						msg: 'Please assign at least one vehicle for driver users',
						toasterType: toasterOptions.error,
					});
					setLoading(false);
					return;
				}

				const createData: CreateUserDto = {
					email: formData.email,
					name: formData.name || '',
					lastName: formData.lastName || '',
					phone: formData.phone || '',
					company: formData.company || '',
					role: roleArray,
					password: formData.password,
					type: formData.type,
					...(formData.type === UserType.DRIVER
						? { vehicles: formData.vehicles }
						: {}),
				};

				await createUser(createData, imageFile);
				toasterNotification({
					msg: 'User created successfully',
					toasterType: toasterOptions.success,
				});
			}
			onSave();
		} catch (error: any) {
			const errorMessage = extractErrorMessage(error) || 'Error saving user';
			toasterNotification({
				msg: errorMessage,
				toasterType: toasterOptions.error,
			});
		} finally {
			setLoading(false);
		}
	};

	return (
		<SideModal open={open} setOpen={handleClose} onClose={onClose}>
			<div style={{ padding: '24px', minWidth: '500px' }}>
				<Typography
					scale="heading4"
					weight="600"
					style={{ marginBottom: '24px' }}
				>
					{user ? 'Edit User' : 'Create User'}
				</Typography>

				<Input
					name="email"
					type="email"
					title="Email"
					value={formData.email}
					onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
						handleChange('email', e.target.value)
					}
					required
				/>

				<Input
					name="name"
					type="text"
					title="Name"
					value={formData.name || ''}
					onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
						handleChange('name', e.target.value)
					}
					required={!user}
				/>

				<Input
					name="lastName"
					type="text"
					title="Last Name"
					value={formData.lastName}
					onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
						handleChange('lastName', e.target.value)
					}
					required={!user}
				/>

				<Input
					name="phone"
					type="text"
					title="Phone"
					value={formData.phone}
					onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
						handleChange('phone', e.target.value)
					}
					required
				/>

				<Input
					name="company"
					type="select"
					title="Company"
					value={formData.company}
					onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
						setFormData((prev) => ({
							...prev,
							company: e.target.value,
							vehicles: [],
						}));
						setVehicleAssignmentMode('single');
					}}
					options={companies.map((c) => ({
						value: c._id,
						label: c.name,
					}))}
				/>

				<Input
					name="type"
					type="select"
					title="User type"
					value={formData.type || UserType.CLIENT}
					onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
						const nextType = e.target.value;
						setFormData((prev) => ({
							...prev,
							type: nextType,
							vehicles: nextType === UserType.DRIVER ? prev.vehicles : [],
						}));
						if (nextType === UserType.DRIVER) {
							setVehicleAssignmentMode('single');
						}
					}}
					options={[
						{ value: UserType.CLIENT, label: 'Client' },
						{ value: UserType.ADMIN, label: 'Admin' },
						{ value: UserType.DRIVER, label: 'Driver' },
					]}
				/>

				{formData.type === UserType.DRIVER && (
					<div style={{ marginTop: '16px' }}>
						<Typography
							scale="small"
							weight="400"
							style={{ marginBottom: '8px' }}
						>
							Assigned vehicles <span style={{ color: 'red' }}>*</span>
						</Typography>
						<Typography
							scale="small"
							weight="400"
							style={{ marginBottom: '12px', color: '#666' }}
						>
							Assign one or more vehicles from the selected company only.
						</Typography>
						<div style={{ display: 'flex', gap: '16px', marginBottom: '12px' }}>
							<label
								htmlFor="vehicle-mode-single"
								style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
							>
								<input
									id="vehicle-mode-single"
									type="radio"
									name="vehicleAssignmentMode"
									checked={vehicleAssignmentMode === 'single'}
									onChange={() => {
										setVehicleAssignmentMode('single');
										setFormData((prev) => ({
											...prev,
											vehicles: prev.vehicles?.length
												? [String(prev.vehicles[0])]
												: [],
										}));
									}}
								/>
								<span>One vehicle</span>
							</label>
							<label
								htmlFor="vehicle-mode-multiple"
								style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
							>
								<input
									id="vehicle-mode-multiple"
									type="radio"
									name="vehicleAssignmentMode"
									checked={vehicleAssignmentMode === 'multiple'}
									onChange={() => setVehicleAssignmentMode('multiple')}
								/>
								<span>Multiple vehicles</span>
							</label>
						</div>
						{!formData.company && (
							<Typography scale="small" weight="400">
								Select a company first to list vehicles.
							</Typography>
						)}
						{formData.company && loadingVehicles && (
							<Typography scale="small" weight="400">
								Loading vehicles...
							</Typography>
						)}
						{formData.company && vehiclesLoadError && (
							<Typography scale="small" weight="400" style={{ color: 'red' }}>
								{vehiclesLoadError}
							</Typography>
						)}
						{formData.company &&
							!loadingVehicles &&
							!vehiclesLoadError &&
							companyVehicles.length === 0 && (
								<Typography scale="small" weight="400">
									No vehicles found for this company.
								</Typography>
							)}
						{formData.company &&
							!loadingVehicles &&
							!vehiclesLoadError &&
							companyVehicles.length > 0 &&
							vehicleAssignmentMode === 'single' && (
								<Input
									name="vehicle"
									type="select"
									title="Vehicle"
									value={singleSelectedVehicleId}
									onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
										const { value } = e.target;
										handleChange('vehicles', value ? [value] : []);
									}}
									options={[
										{ value: '', label: 'Select a vehicle' },
										...companyVehicles.map((vehicle) => ({
											value: String(vehicle._id),
											label: `${vehicle.internalId} — ${vehicle.description}`,
										})),
									]}
								/>
							)}
						{vehicleAssignmentMode === 'multiple' &&
							companyVehicles.map((vehicle) => (
								<div key={vehicle._id} style={{ marginBottom: '8px' }}>
									<label
										htmlFor={`vehicle-${vehicle._id}`}
										style={{
											display: 'flex',
											alignItems: 'center',
											gap: '8px',
										}}
									>
										<input
											id={`vehicle-${vehicle._id}`}
											type="checkbox"
											checked={selectedVehicleIds.includes(String(vehicle._id))}
											onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
												const vehicleId = String(vehicle._id);
												if (e.target.checked) {
													handleChange('vehicles', [
														...selectedVehicleIds,
														vehicleId,
													]);
												} else {
													handleChange(
														'vehicles',
														selectedVehicleIds.filter((id) => id !== vehicleId),
													);
												}
											}}
										/>
										<span>
											{vehicle.internalId} — {vehicle.description}
										</span>
									</label>
								</div>
							))}
					</div>
				)}

				<div style={{ marginTop: '16px' }}>
					<Typography
						scale="small"
						weight="400"
						style={{ marginBottom: '8px' }}
					>
						Roles <span style={{ color: 'red' }}>*</span>
					</Typography>
					{Array.isArray(roles) &&
						roles.map((role) => (
							<div key={role._id} style={{ marginBottom: '8px' }}>
								<label
									htmlFor={role._id}
									style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
								>
									<input
										type="checkbox"
										checked={
											Array.isArray(formData.role) &&
											formData.role.includes(role._id)
										}
										onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
											const currentRoles = Array.isArray(formData.role)
												? formData.role
												: [];
											if (e.target.checked) {
												handleChange('role', [...currentRoles, role._id]);
											} else {
												handleChange(
													'role',
													currentRoles.filter((r) => r !== role._id),
												);
											}
										}}
									/>
									<span>{role.name}</span>
								</label>
							</div>
						))}
				</div>

				{!user && (
					<Input
						name="password"
						type="password"
						title="Password"
						value={(formData as CreateUserDto).password || ''}
						onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
							handleChange('password', e.target.value)
						}
						required={!user}
					/>
				)}

				<div style={{ marginTop: '16px' }}>
					<Typography
						scale="small"
						weight="400"
						style={{ marginBottom: '8px' }}
					>
						Profile Image
					</Typography>
					<input
						type="file"
						accept="image/*"
						onChange={handleImageChange}
						style={{ width: '100%' }}
					/>
				</div>

				<div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
					<Button variant="principal" onClick={handleSubmit} disabled={loading}>
						{loading ? 'Saving...' : 'Save'}
					</Button>
					<Button variant="outline" onClick={onClose}>
						Cancel
					</Button>
				</div>
			</div>
		</SideModal>
	);
}

export default EditUser;
